import React from 'react';
import { DisplayRibbonsHomepage, isTestVariant } from '@noths/polaris-client-experiments';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { getOrSetRichRelevanceSessionId } from '@noths/polaris-client-user-configuration';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';

import { LegacyHomepageData, LegacyHomepageProps } from 'src/components/templates/LegacyHomepage';
import { ContentData } from 'src/components/templates/RibbonsPage';
import * as contentstackService from 'src/services/contentstack';
import { NextConfig } from 'src/types/NextConfig';
import { PageProps } from 'src/types/PageProps';
import { TransformerOptions } from 'src/types/Transformer';
import { fetchPage } from 'src/utils/fetchPage';
import { getExperiments } from 'src/utils/getExperiments';
import homepageData from 'src/utils/transformers/homepageData';
import { RibbonsPageProps } from '../components/templates/RibbonsPage';
import { MAIN_CONTENT_WRAPPER } from '../constants/elementIds';
import { transformSeo } from '../content/transformSeo';
import { transformToBlocks } from '../content/transformToBlocks';

const { publicRuntimeConfig } = getConfig() as NextConfig;
const HOMEPAGE = 'homepage';

const LegacyHomepage = dynamic<LegacyHomepageProps>(() =>
  import('src/components/templates/LegacyHomepage').then((mod) => mod.LegacyHomepage),
);
const RibbonsPage = dynamic<RibbonsPageProps>(() =>
  import('src/components/templates/RibbonsPage').then((mod) => mod.RibbonsPage),
);

interface HomepageProps extends PageProps {
  showRibbonsHomepage: boolean;
}

const isRibbonsHomepage = (
  page: LegacyHomepageData | ContentData,
  isRibbonsHomepage: boolean,
): page is ContentData => isRibbonsHomepage;

const Homepage = ({ showRibbonsHomepage, page, ...props }: HomepageProps) => (
  <div id={MAIN_CONTENT_WRAPPER}>
    {isRibbonsHomepage(page, showRibbonsHomepage) ? (
      <RibbonsPage title={page.title} seo={page.seo} contentBlocks={page.blocks} {...props} />
    ) : (
      <LegacyHomepage page={page} {...props} />
    )}
  </div>
);

const getPageData = async (
  showRibbonsHomepage: boolean,
  transformerOptions: TransformerOptions,
  isPreview: boolean,
) => {
  const {
    contentStack: { campaignStack, ribbonsStack },
  } = publicRuntimeConfig;

  if (showRibbonsHomepage) {
    const { sections, seo, title } = await fetchPage(ribbonsStack, HOMEPAGE, '/', null, isPreview);

    return {
      title,
      seo: transformSeo(seo),
      blocks: sections ? await transformToBlocks(sections, transformerOptions) : null,
    };
  } else {
    return await fetchPage(campaignStack, HOMEPAGE, '/', homepageData, isPreview);
  }
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, res } = context;
  const { currency: currencyFromHeader } = parseLocaleSetCookieHeaders(res.getHeaders());
  const { currency: currencyFromCookie, rr_session } = req.cookies;
  const currency = currencyFromHeader ?? currencyFromCookie;
  const isPreview = !!context.preview;
  const urlPath = req.url;
  const richRelevanceSessionId = getOrSetRichRelevanceSessionId(context.res, rr_session);
  const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies);
  const { NODE_ENV: env } = process.env;
  const experiments = (req && getExperiments(req)) ?? '';
  const showRibbonsHomepage = isTestVariant(DisplayRibbonsHomepage, experiments);
  const [navData, topBannerData] = await contentstackService.fetchData(urlPath);

  const transformerOptions = {
    page: HOMEPAGE,
    currency,
    richRelevanceSessionId,
    functionalCookiesConsented,
  } as TransformerOptions;
  const pageData = await getPageData(showRibbonsHomepage, transformerOptions, isPreview);

  return {
    props: {
      page: pageData,
      environment: env,
      isPreview,
      category: 'Home',
      richRelevanceSessionId,
      functionalCookiesConsented,
      navData,
      topBannerData,
      currency,
      showRibbonsHomepage,
    },
  };
};

export default Homepage;
